import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { OverviewData, RevenueMonthPoint } from '../hooks/useOverviewData';
import { useCountUp } from '../hooks/useCountUp';
import Sparkline from './Sparkline';
import {
    formatCurrency,
    formatDuration,
    formatNumber,
    formatPercent,
} from '../utils/format';

interface Props {
    data: OverviewData | null;
    revenueTrend?: RevenueMonthPoint[] | null;  // 12-month series for the hero sparkline
    loading: boolean;
    error: string | null;
}

const HeroStrip: React.FC<Props> = ({ data, revenueTrend, loading, error }) => {
    const intl = useIntl();
    const locale = intl.locale;

    if (error) {
        return (
            <div className="av2-error-banner">
                {error === 'permission_denied' ? (
                    <FormattedMessage
                        id="analyticsV2.error.permission"
                        defaultMessage="You don't have permission to see this dashboard."
                    />
                ) : (
                    <FormattedMessage
                        id="analyticsV2.error.generic"
                        defaultMessage="Couldn't load the dashboard. Try refreshing."
                    />
                )}
            </div>
        );
    }

    if (loading || !data) {
        return <HeroStripSkeleton />;
    }

    const sparkValues = (revenueTrend ?? []).map((p) => p.revenue);

    return (
        <div className="av2-hero">
            <HeroKpiCard
                label={<FormattedMessage id="analyticsV2.kpi.revenue" defaultMessage="Revenue" />}
                value={data.pulse.revenue.current}
                delta={data.pulse.revenue.delta_pct}
                format={(v) => formatCurrency(v, locale)}
                sparkline={
                    sparkValues.length >= 2 ? (
                        // Hero card is dark navy in the v2 palette — pass white-tinted
                        // colors so the sparkline reads against the inverted surface.
                        <Sparkline
                            values={sparkValues}
                            strokeColor="rgba(255, 255, 255, 0.85)"
                            fillColor="rgba(255, 255, 255, 0.14)"
                        />
                    ) : null
                }
            />

            <div className="av2-hero__pulses">
                <PulseKpiCard
                    label={
                        <FormattedMessage
                            id="analyticsV2.kpi.activePipeline"
                            defaultMessage="Ordered products"
                        />
                    }
                    hint={
                        <FormattedMessage
                            id="analyticsV2.kpi.activePipelineHint"
                            defaultMessage="products active in production right now"
                        />
                    }
                    value={data.pulse.active_pipeline_count.current}
                    format={(v) => formatNumber(v, locale)}
                />
                <PulseKpiCard
                    label={
                        <FormattedMessage
                            id="analyticsV2.kpi.completions"
                            defaultMessage="Steps completed"
                        />
                    }
                    hint={
                        <FormattedMessage
                            id="analyticsV2.kpi.completionsHint"
                            defaultMessage="production steps finished in this period"
                        />
                    }
                    value={data.pulse.completions_count.current}
                    delta={data.pulse.completions_count.delta_pct}
                    format={(v) => formatNumber(v, locale)}
                />
                <PulseKpiCard
                    label={
                        <FormattedMessage
                            id="analyticsV2.kpi.cycleTime"
                            defaultMessage="Average step duration"
                        />
                    }
                    hint={
                        <FormattedMessage
                            id="analyticsV2.kpi.cycleTimeHint"
                            defaultMessage="how long a single production step takes on average"
                        />
                    }
                    value={data.pulse.avg_cycle_time_seconds.current}
                    delta={data.pulse.avg_cycle_time_seconds.delta_pct}
                    rawDirectionInverted
                    format={(v) => formatDuration(v)}
                />
                <PulseKpiCard
                    label={
                        <FormattedMessage
                            id="analyticsV2.kpi.reworkRate"
                            defaultMessage="Rework rate"
                        />
                    }
                    hint={
                        <FormattedMessage
                            id="analyticsV2.kpi.reworkRateHint"
                            defaultMessage="% of steps that had to be redone"
                        />
                    }
                    value={data.pulse.rework_rate_pct.current}
                    delta={data.pulse.rework_rate_pct.delta_pct}
                    rawDirectionInverted
                    format={(v) => formatPercent(v)}
                />
            </div>
        </div>
    );
};

// ── Hero: the one big number ─────────────────────────────────────────────────

interface HeroKpiCardProps {
    label: React.ReactNode;
    value: number | null;
    delta: number | null;
    format: (v: number) => string;
    sparkline?: React.ReactNode;
}

const HeroKpiCard: React.FC<HeroKpiCardProps> = ({ label, value, delta, format, sparkline }) => {
    const animated = useCountUp(value);
    const display = value == null ? '—' : format(animated);
    return (
        <div className="av2-hero__main">
            <div className="av2-hero__label">{label}</div>
            <div className="av2-hero__value">{display}</div>
            <Delta delta={delta} />
            {sparkline && <div className="av2-hero__spark">{sparkline}</div>}
        </div>
    );
};

// ── Pulse: the smaller supporting cards ──────────────────────────────────────

interface PulseKpiCardProps {
    label: React.ReactNode;
    /** Small caption under the value explaining what the number actually counts. */
    hint?: React.ReactNode;
    value: number | null;
    delta?: number | null;
    /**
     * When true, the underlying raw metric has reversed semantics ("lower is
     * better" — cycle time, rework rate). The backend has already inverted
     * the sign for color purposes, so delta_pct positive = improvement.
     * This flag flips the arrow glyph back to the raw direction so the user
     * reads "cycle time went down" (↓) instead of "improvement of cycle time" (↑).
     */
    rawDirectionInverted?: boolean;
    format: (v: number) => string;
}

const PulseKpiCard: React.FC<PulseKpiCardProps> = ({
    label,
    hint,
    value,
    delta,
    rawDirectionInverted,
    format,
}) => {
    const animated = useCountUp(value);
    const display = value == null ? '—' : format(animated);
    return (
        <div className="av2-pulse">
            <div className="av2-pulse__label">{label}</div>
            <div className="av2-pulse__value">{display}</div>
            {hint && <div className="av2-pulse__hint">{hint}</div>}
            {delta != null && <Delta delta={delta} rawDirectionInverted={rawDirectionInverted} />}
        </div>
    );
};

// ── Delta pill ───────────────────────────────────────────────────────────────
//
// Backend convention: delta_pct is ALREADY sign-flipped for "lower is better"
// metrics (cycle time, rework rate). So positive always = improvement = green,
// negative always = regression = red. No sign math in the color.
//
// The `rawDirectionInverted` prop only flips the arrow glyph so the user
// reads it as the raw movement of the underlying number (cycle time really
// went *down* even though the color says "up").

interface DeltaProps {
    delta: number | null;
    rawDirectionInverted?: boolean;
}

const Delta: React.FC<DeltaProps> = ({ delta, rawDirectionInverted }) => {
    if (delta == null) return null;
    const improved = delta > 0.05;
    const worsened = delta < -0.05;
    const tone: 'positive' | 'negative' | 'neutral' = improved
        ? 'positive'
        : worsened
        ? 'negative'
        : 'neutral';

    // Arrow shows the raw direction. For non-inverted metrics this matches
    // the delta sign. For inverted metrics it shows the opposite, because
    // the backend already flipped the sign for the color tone.
    let arrow: string;
    if (rawDirectionInverted) {
        arrow = improved ? '↓' : worsened ? '↑' : '·';
    } else {
        arrow = improved ? '↑' : worsened ? '↓' : '·';
    }

    return (
        <div className={`av2-delta av2-delta--${tone}`}>
            <span className="av2-delta__arrow" aria-hidden="true">
                {arrow}
            </span>
            <span>{Math.abs(delta).toFixed(1)}%</span>
        </div>
    );
};

// ── Skeleton (shown while loading) ───────────────────────────────────────────

const HeroStripSkeleton: React.FC = () => {
    return (
        <div className="av2-hero">
            <div className="av2-hero__main av2-hero__main--skeleton">
                <div className="av2-skel av2-skel--label" />
                <div className="av2-skel av2-skel--hero-value" />
                <div className="av2-skel av2-skel--delta" />
                <div className="av2-skel av2-skel--sparkline" />
            </div>
            <div className="av2-hero__pulses">
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="av2-pulse av2-pulse--skeleton">
                        <div className="av2-skel av2-skel--label" />
                        <div className="av2-skel av2-skel--pulse-value" />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default HeroStrip;
